import { parseLooseNumber } from '@kia/shared';

/**
 * The four MONTH-level pricing levers (AssumptionMasterCard) are entered as PERCENT strings and each is a
 * deliberate tri-state override of the global Settings lever:
 *
 *   ''  (blank)   → null      = NOT overridden; the global config lever applies.
 *   a number      → fraction  = value ÷ 100, e.g. «۳۵» → 0.35, via parseLooseNumber (the one Persian/Arabic
 *                               digit authority, @kia/shared) so a Persian entry no longer becomes NaN.
 *   garbage       → BLOCK     = a NON-EMPTY string that parses to NaN is NOT collapsed to null. The server
 *                               reads null as "not overridden", so the old `Number(s)/100`→NaN→JSON→null path
 *                               SILENTLY DISCARDED the operator's entry and reported success (WEB-03). We stop
 *                               the save and name the offending field so the operator sees it.
 *
 * Range is NOT enforced here on purpose. The server's optionalPct (routes/assumptions.ts) is the ONE bounds
 * authority — floor/target ∈ [0, 0.99], freight ∈ [0, 1], floatPct ∈ [0, 0.5] — and these fields are in
 * percent space (×100) while those bounds are fractions, so any client clamp would be both redundant and,
 * in percent space, wrong (it would snap 35 to 0.99). We pass the parsed fraction through and let the server
 * return its Persian 400 on an out-of-range value.
 */
export type MonthLeverField = 'freightPct' | 'floor' | 'target' | 'floatPct';

export interface MonthLeverInput {
  freightPct: string;
  floor: string;
  target: string;
  floatPct: string;
}

export interface MonthLeverOverrides {
  freightPct: number | null;
  floor: number | null;
  target: number | null;
  floatPct: number | null;
}

export type ParseMonthLeversResult =
  { ok: true; overrides: MonthLeverOverrides } | { ok: false; field: MonthLeverField };

const FIELDS: MonthLeverField[] = ['freightPct', 'floor', 'target', 'floatPct'];

export function parseMonthLevers(input: MonthLeverInput): ParseMonthLeversResult {
  // '' → null (not overridden); otherwise the Persian-aware fraction (may be NaN for garbage → caught below).
  const pctOrNull = (s: string): number | null =>
    s.trim() === '' ? null : parseLooseNumber(s) / 100;
  const overrides = {} as MonthLeverOverrides;
  for (const f of FIELDS) {
    const v = pctOrNull(input[f]);
    if (v !== null && Number.isNaN(v)) return { ok: false, field: f };
    overrides[f] = v;
  }
  return { ok: true, overrides };
}
